import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, CircularProgress, Card, CardContent } from '@material-ui/core';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import PhoneIcon from '@material-ui/icons/Phone';
import useYext from '../hooks/useYext';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    position: 'relative',
    zIndex: 1,
    backgroundColor: '#FFFFFF',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 250px))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    },
  },
  locationCard: {
    position: 'relative',
    padding: '16px',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: '#FFFFFF',
    border: '2px solid #D1D5DB',
    boxShadow: 'none',
    minWidth: '200px',
    maxWidth: '250px',
    '&:hover': {
      borderColor: '#60A5FA',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
  },
  locationCardSelected: {
    backgroundColor: '#EFF6FF',
    border: '2px solid #3B82F6',
    boxShadow: 'none',
    cursor: 'default',
    '&:hover': {
      backgroundColor: '#EFF6FF',
      borderColor: '#3B82F6',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
  },
  locationCardDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    '&:hover': {
      borderColor: '#D1D5DB',
      boxShadow: 'none',
    },
  },
  cardContent: {
    padding: 0,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  locationTitle: {
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: theme.spacing(1),
    fontSize: '1rem',
    lineHeight: 1.3,
  },
  locationInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  infoRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
    color: '#6B7280',
  },
  infoText: {
    color: '#6B7280',
    fontSize: '0.875rem',
    lineHeight: 1.4,
    flex: 1,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60px',
  },
}));

const LocationSelector = () => {
  const classes = useStyles();
  const { locations, currentLocation, selectLocation, scanLocation, isFetching, isScanning } = useYext();

  const handleCardClick = async (location) => {
    if (currentLocation?.id === location.id || isScanning) {
      return;
    }

    selectLocation(location);
    
    await scanLocation(location.id);
  };

  if (isFetching) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!locations || locations.length === 0) {
    return (
      <Box className={classes.container}>
        <Typography variant="h6" gutterBottom>
          Select Location
        </Typography>
        <Typography variant="body2" color="textSecondary">
          No locations available
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Typography variant="h6" gutterBottom>
        Select Location
      </Typography>
      
      <Box className={classes.cardsGrid}>
        {locations.map((location) => {
          const isSelected = currentLocation?.id === location.id;
          const isDisabled = isScanning;
          
          return (
            <Card
              key={location.id}
              className={`${classes.locationCard} ${
                isSelected ? classes.locationCardSelected : ''
              } ${isDisabled ? classes.locationCardDisabled : ''}`}
              onClick={() => handleCardClick(location)}
            >
              <CardContent className={classes.cardContent}>
                <Typography className={classes.locationTitle}>
                  {location.business_name || location.name || 'Unnamed Location'}
                </Typography>
                
                <Box className={classes.locationInfo}>
                  <Box className={classes.infoRow}>
                    <LocationOnIcon fontSize="small" />
                    <Typography variant="body2" className={classes.infoText}>
                      {location.address || 'No address available'}
                    </Typography>
                  </Box>
                  
                  {location.phone && (
                    <Box className={classes.infoRow}>
                      <PhoneIcon fontSize="small" />
                      <Typography variant="body2" className={classes.infoText}>
                        {location.phone}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default LocationSelector;
